// frontend/src/components/integrations/IntegrationCategoryCard.tsx
import { CheckCircle2 } from "lucide-react";
import React from "react";
import { useNavigate } from "react-router";
import { Card } from "src/components/ui/card";

import { IntegrationCategory } from "./integration-categories";

interface IntegrationCategoryCardProps {
  category: IntegrationCategory;
  connectedTypes: string[];
  basePath?: string;
  variant?: "onboarding" | "settings";
  isRequired?: boolean;
  connectedCountOverride?: number;
  connectedLabel?: string;
  manageLabel?: string;
}

export default function IntegrationCategoryCard({
  category,
  connectedTypes,
  basePath = "/onboarding/integrations",
  variant = "onboarding",
  isRequired = false,
  connectedCountOverride,
  connectedLabel,
  manageLabel,
}: IntegrationCategoryCardProps) {
  const navigate = useNavigate();

  const connectedCount = connectedCountOverride ?? category.integrationTypes.filter(type =>
    connectedTypes.includes(type)
  ).length;

  const isConnected = connectedCount > 0;

  const Icon = category.icon;

  const handleClick = () => {
    navigate(`${basePath}/${category.urlSlug}`);
  };

  const handleLinkClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`${basePath}/${category.urlSlug}`);
  };

  const showGreenBorder = variant === "onboarding" && isConnected;
  const showCheckmark = variant === "onboarding" && isConnected;

  return (
    <Card
      interactive
      onClick={handleClick}
      className={`
        p-4 hover:bg-accent
        ${showGreenBorder ? "border-green-500" : ""}
        flex flex-col h-full min-h-[130px]
      `}
    >
      <div className="flex items-center gap-3 mb-1">
        {showCheckmark ? (
          <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
        ) : (
          <Icon className="h-5 w-5 text-muted-foreground flex-shrink-0" />
        )}
        <h3 className="text-foreground font-medium">{category.title}</h3>
        {isRequired && !isConnected && (
          <span className="text-xs font-medium text-amber-400 bg-amber-400/10 px-2 py-0.5 rounded">
            Required
          </span>
        )}
      </div>

      {isConnected && (
        <div className="flex items-center gap-1.5 pl-8 mb-2">
          <div className="h-2 w-2 rounded-full bg-green-500" />
          <span className="text-green-500 text-xs">{connectedLabel ?? `${connectedCount} connected`}</span>
        </div>
      )}

      <p className={`text-muted-foreground text-sm flex-grow mb-3 pl-8 ${!isConnected ? "mt-1" : ""}`}>
        {category.description}
      </p>

      <button
        onClick={handleLinkClick}
        className="text-primary hover:text-primary/80 text-sm font-medium text-left transition-colors pl-8"
      >
        {manageLabel ?? "Manage"}
      </button>
    </Card>
  );
}
